import { useState, useEffect, useContext, type ReactNode } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Header } from "../../components/layout/Header";
import { useTheme, type Theme } from "../../contexts/ThemeContext";
import { useSettings } from "../../contexts/SettingsContext";
import { AuthContext } from "../../contexts/AuthContext";
import { useAIConfig } from "../../contexts/AIConfigContext";
import { aiAPI, authAPI, type AIProvider } from "../../lib/api";
import styles from "./SettingsPage.module.css";

const PROVIDER_KEY_URLS: Record<AIProvider, { url: string; labelKey: string }> = {
  anthropic: {
    url: "https://console.anthropic.com/settings/keys",
    labelKey: "settings.apiConfig.getAnthropicKey",
  },
  openai: {
    url: "https://platform.openai.com/api-keys",
    labelKey: "settings.apiConfig.getOpenAIKey",
  },
  custom: {
    url: "",
    labelKey: "",
  },
};

const PROVIDER_PLACEHOLDERS: Record<AIProvider, string> = {
  anthropic: "placeholderAnthropic",
  openai: "placeholderOpenAI",
  custom: "placeholderCustom",
};

const API_KEY_PATTERNS: Record<AIProvider, { regex: RegExp; hint: string }> = {
  anthropic: {
    regex: /^sk-ant-api\d{2}-.+$/,
    hint: "Anthropic keys start with sk-ant-api followed by a version number (e.g. sk-ant-api03-…)",
  },
  openai: {
    regex: /^sk-.+$/,
    hint: "OpenAI keys start with sk-",
  },
  custom: {
    regex: /^.+$/,
    hint: "Enter the API key for your custom endpoint",
  },
};

const DEFAULT_CUSTOM_BASE_URL = "http://localhost:11434/v1";

function validateApiKey(key: string, provider: AIProvider): string | null {
  const trimmed = key.trim();
  if (!trimmed) return null; // empty is handled by disabled button
  const pattern = API_KEY_PATTERNS[provider];
  if (!pattern.regex.test(trimmed)) {
    return pattern.hint;
  }
  return null;
}

type SettingsTab = "general" | "ai" | "voice" | "account";

const VALID_TABS: readonly SettingsTab[] = ["general", "ai", "voice", "account"];

function isValidTab(value: string | null): value is SettingsTab {
  return value !== null && (VALID_TABS as readonly string[]).includes(value);
}

const TAB_ICONS: Record<SettingsTab, ReactNode> = {
  general: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      width="18"
      height="18"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" />
    </svg>
  ),
  ai: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      width="18"
      height="18"
    >
      <path d="M12 2a4 4 0 014 4v1h2a2 2 0 012 2v10a2 2 0 01-2 2H6a2 2 0 01-2-2V9a2 2 0 012-2h2V6a4 4 0 014-4z" />
      <circle cx="9" cy="14" r="1" />
      <circle cx="15" cy="14" r="1" />
    </svg>
  ),
  voice: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      width="18"
      height="18"
    >
      <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
      <path d="M19 10v2a7 7 0 01-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="23" />
      <line x1="8" y1="23" x2="16" y2="23" />
    </svg>
  ),
  account: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      width="18"
      height="18"
    >
      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
};

export default function SettingsPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const returnTo = searchParams.get("returnTo");
  const initialTabParam = searchParams.get("tab");
  const initialTab = isValidTab(initialTabParam) ? initialTabParam : "general";
  const [activeTab, setActiveTab] = useState<SettingsTab>(initialTab);
  const { theme, setTheme } = useTheme();
  const { settings, setAIEnabled, updateAutoSave, updateAIBehavior, resetToDefaults } =
    useSettings();
  const { aiConfigured, aiHasConfig, refreshAIStatus } = useAIConfig();
  const [selectedProvider, setSelectedProvider] = useState<AIProvider>("anthropic");
  const [apiKeyInput, setApiKeyInput] = useState("");
  const [baseUrlInput, setBaseUrlInput] = useState("");
  const [modelNameInput, setModelNameInput] = useState("");
  const [isSavingKey, setIsSavingKey] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [verifyResult, setVerifyResult] = useState<{
    valid: boolean;
    error?: string;
  } | null>(null);
  const [apiKeyError, setApiKeyError] = useState<string | null>(null);
  const [userHasInteracted, setUserHasInteracted] = useState(false);
  const [isTesting, setIsTesting] = useState(false);

  // Account settings state
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();
  const [newEmail, setNewEmail] = useState("");
  const [emailPassword, setEmailPassword] = useState("");
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  // Sync active tab with URL search params (handles back/forward navigation)
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    const target = isValidTab(tabParam) ? tabParam : "general";
    if (target !== activeTab) {
      setActiveTab(target);
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync provider selection from server status on mount,
  // and auto-verify the key so the badge reflects actual validity (#257)
  useEffect(() => {
    aiAPI
      .getStatus()
      .then(async (status) => {
        if (status.provider) {
          setSelectedProvider(status.provider);
        }
        if (status.configured || status.needsVerification) {
          try {
            const result = await aiAPI.verify();
            setVerifyResult(result);
            if (result.valid) {
              await refreshAIStatus();
            }
          } catch {
            setVerifyResult({
              valid: false,
              error: "Could not reach the server",
            });
          }
        }
      })
      .catch(() => {
        /* context already handles configured state */
      });
  }, []);

  const handleTabChange = (tab: SettingsTab) => {
    setActiveTab(tab);
    const params = new URLSearchParams(searchParams);
    if (tab === "general") {
      params.delete("tab");
    } else {
      params.set("tab", tab);
    }
    setSearchParams(params, { replace: true });
  };

  const handleSaveApiKey = async () => {
    setUserHasInteracted(true);
    if (selectedProvider !== "custom" && !apiKeyInput.trim()) return;
    if (selectedProvider === "custom" && !baseUrlInput.trim()) return;
    if (selectedProvider === "custom" && !modelNameInput.trim()) return;
    if (apiKeyInput.trim()) {
      const formatError = validateApiKey(apiKeyInput, selectedProvider);
      if (formatError) {
        setApiKeyError(formatError);
        return;
      }
    }
    setIsSavingKey(true);
    setVerifyResult(null);
    setApiKeyError(null);
    try {
      await aiAPI.configure(
        apiKeyInput.trim(),
        selectedProvider,
        selectedProvider === "custom" ? baseUrlInput.trim() : undefined,
        selectedProvider === "custom" ? modelNameInput.trim() : undefined,
      );

      // Validate the key by making a test request (15s frontend timeout)
      let result: { valid: boolean; error?: string };
      try {
        const verifyPromise = aiAPI.verify();
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error(t("settings.apiConfig.connectionTimeout"))), 15_000),
        );
        result = await Promise.race([verifyPromise, timeoutPromise]);
      } catch (verifyErr: unknown) {
        // Verification failed at the network level — roll back
        await aiAPI.removeConfigure();
        await refreshAIStatus();
        const detail =
          verifyErr instanceof Error ? verifyErr.message : t("settings.apiConfig.couldNotVerify");
        setVerifyResult({ valid: false, error: detail });
        toast.error(detail);
        return;
      }

      if (!result.valid) {
        // Key is invalid — roll back the configuration
        await aiAPI.removeConfigure();
        await refreshAIStatus();
        setVerifyResult(result);
        toast.error(result.error || t("settings.apiConfig.invalidKey", "Invalid API key"));
        return;
      }

      await refreshAIStatus();
      setApiKeyInput("");
      setVerifyResult({ valid: true });
      toast.success(t("settings.apiConfig.saveSuccess"));
    } catch {
      // configure() itself failed — roll back to be safe
      try {
        await aiAPI.removeConfigure();
        await refreshAIStatus();
      } catch {
        // best-effort rollback
      }
      toast.error(t("settings.apiConfig.saveError"));
    } finally {
      setIsSavingKey(false);
    }
  };

  const handleRemoveApiKey = async () => {
    setIsSavingKey(true);
    try {
      await aiAPI.removeConfigure();
      await refreshAIStatus();
      setBaseUrlInput("");
      setModelNameInput("");
      setApiKeyInput("");
      setVerifyResult(null);
      setApiKeyError(null);
      toast.success(t("settings.apiConfig.removeSuccess"));
    } catch {
      toast.error(t("settings.apiConfig.removeError"));
    } finally {
      setIsSavingKey(false);
    }
  };

  const handleTestConnection = async () => {
    setUserHasInteracted(true);
    setIsTesting(true);

    setVerifyResult(null);
    try {
      // Temporarily save the current form values to test them
      if (apiKeyInput.trim()) {
        const formatError = validateApiKey(apiKeyInput, selectedProvider);
        if (formatError) {
          setApiKeyError(formatError);
          return;
        }
      }
      await aiAPI.configure(
        apiKeyInput.trim(),
        selectedProvider,
        selectedProvider === "custom" ? baseUrlInput.trim() : undefined,
        selectedProvider === "custom" ? modelNameInput.trim() : undefined,
      );

      const verifyPromise = aiAPI.verify();
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error(t("settings.apiConfig.connectionTimeout"))), 15_000),
      );
      const result = await Promise.race([verifyPromise, timeoutPromise]);

      if (result.valid) {
        setVerifyResult({ valid: true });
        await refreshAIStatus();
        toast.success(t("settings.apiConfig.verifySuccess"));
      } else {
        setVerifyResult(result);
        toast.error(result.error || t("settings.apiConfig.verifyFailed"));
      }

      // Roll back — this was just a test, don't persist
      if (!aiConfigured) {
        await aiAPI.removeConfigure();
        await refreshAIStatus();
      }
    } catch (err: unknown) {
      const detail =
        err instanceof Error ? err.message : t("settings.apiConfig.couldNotReachServer");
      setVerifyResult({ valid: false, error: detail });
      toast.error(detail);
      // Roll back on error too
      if (!aiConfigured) {
        try {
          await aiAPI.removeConfigure();
          await refreshAIStatus();
        } catch {
          // best-effort
        }
      }
    } finally {
      setIsTesting(false);
    }
  };

  const handleThemeChange = (newTheme: Theme) => {
    setTheme(newTheme);
  };

  const handleAutoSaveToggle = () => {
    updateAutoSave({ enabled: !settings.autoSave.enabled });
  };

  const handleAutoSaveIntervalChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    updateAutoSave({ intervalSeconds: parseInt(e.target.value, 10) });
  };

  const handleSuggestionFrequencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    updateAIBehavior({
      suggestionFrequency: e.target.value as "minimal" | "balanced" | "aggressive",
    });
  };

  const handleInlineSuggestionsToggle = () => {
    updateAIBehavior({
      showInlineSuggestions: !settings.aiBehavior.showInlineSuggestions,
    });
  };

  const handleAutoAcceptMinorToggle = () => {
    updateAIBehavior({ autoAcceptMinor: !settings.aiBehavior.autoAcceptMinor });
  };

  const handleUpdateEmail = async () => {
    if (!newEmail.trim() || !emailPassword) return;
    setIsUpdatingEmail(true);
    try {
      await authAPI.updateEmail({
        email: newEmail.trim(),
        password: emailPassword,
      });
      toast.success("Email updated. Please verify your new email address.");
      setNewEmail("");
      setEmailPassword("");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to update email";
      toast.error(msg);
    } finally {
      setIsUpdatingEmail(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) return;
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    setIsChangingPassword(true);
    try {
      await authAPI.changePassword({ currentPassword, newPassword });
      toast.success("Password changed successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to change password";
      toast.error(msg);
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== "DELETE" || !deletePassword) return;
    setIsDeletingAccount(true);
    try {
      await authAPI.deleteAccount({ password: deletePassword });
      toast.success("Account deleted");
      navigate("/login");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to delete account";
      toast.error(msg);
    } finally {
      setIsDeletingAccount(false);
    }
  };

  const handleResetToDefaults = () => {
    resetToDefaults();
    toast.success(t("settings.resetSuccess"));
  };

  const tabs: { id: SettingsTab; label: string }[] = [
    { id: "general", label: t("settings.tabs.general") },
    { id: "ai", label: t("settings.tabs.ai") },
    { id: "voice", label: t("settings.tabs.voice") },
    { id: "account", label: t("settings.tabs.account") },
  ];

  return (
    <>
      <Header />
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            {returnTo && (
              <Link to={returnTo} className={styles.backLink}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  width="16"
                  height="16"
                >
                  <polyline points="15 18 9 12 15 6" />
                </svg>
                {t("settings.backToProject")}
              </Link>
            )}
            <h1 className={styles.title}>{t("settings.title")}</h1>
            <p className={styles.subtitle}>{t("settings.subtitle")}</p>
          </div>
        </div>

        <div className={styles.layout}>
          <nav className={styles.sidebar}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`${styles.tabButton} ${activeTab === tab.id ? styles.tabActive : ""}`}
                onClick={() => handleTabChange(tab.id)}
              >
                {TAB_ICONS[tab.id]}
                {tab.label}
              </button>
            ))}
          </nav>

          <div className={styles.content}>
            {activeTab === "general" && (
              <>
                <div className={styles.section}>
                  <h2 className={styles.sectionTitle}>{t("settings.appearance.title")}</h2>
                  <p className={styles.sectionDescription}>
                    {t("settings.appearance.description")}
                  </p>

                  <div className={styles.settingRow}>
                    <div className={styles.settingInfo}>
                      <label className={styles.settingLabel}>
                        {t("settings.appearance.theme")}
                      </label>
                      <p className={styles.settingDescription}>
                        {t("settings.appearance.themeDescription")}
                      </p>
                    </div>
                    <div className={styles.themeOptions}>
                      <button
                        type="button"
                        className={`${styles.themeButton} ${theme === "light" ? styles.active : ""}`}
                        onClick={() => handleThemeChange("light")}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="5" />
                          <line x1="12" y1="1" x2="12" y2="3" />
                          <line x1="12" y1="21" x2="12" y2="23" />
                          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                          <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                          <line x1="1" y1="12" x2="3" y2="12" />
                          <line x1="21" y1="12" x2="23" y2="12" />
                          <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                          <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                        </svg>
                        {t("theme.light")}
                      </button>
                      <button
                        type="button"
                        className={`${styles.themeButton} ${theme === "dark" ? styles.active : ""}`}
                        onClick={() => handleThemeChange("dark")}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                        </svg>
                        {t("theme.dark")}
                      </button>
                      <button
                        type="button"
                        className={`${styles.themeButton} ${theme === "system" ? styles.active : ""}`}
                        onClick={() => handleThemeChange("system")}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                          <line x1="8" y1="21" x2="16" y2="21" />
                          <line x1="12" y1="17" x2="12" y2="21" />
                        </svg>
                        {t("theme.system")}
                      </button>
                    </div>
                  </div>
                </div>

                <div className={styles.section}>
                  <h2 className={styles.sectionTitle}>{t("settings.autoSave.title")}</h2>
                  <p className={styles.sectionDescription}>{t("settings.autoSave.description")}</p>

                  <div className={styles.settingRow}>
                    <div className={styles.settingInfo}>
                      <label className={styles.settingLabel}>{t("settings.autoSave.enable")}</label>
                      <p className={styles.settingDescription}>
                        {t("settings.autoSave.enableDescription")}
                      </p>
                    </div>
                    <button
                      type="button"
                      className={`${styles.toggle} ${settings.autoSave.enabled ? styles.toggleOn : ""}`}
                      onClick={handleAutoSaveToggle}
                      aria-pressed={settings.autoSave.enabled}
                    >
                      <span className={styles.toggleThumb} />
                    </button>
                  </div>

                  {settings.autoSave.enabled && (
                    <div className={styles.settingRow}>
                      <div className={styles.settingInfo}>
                        <label className={styles.settingLabel}>
                          {t("settings.autoSave.interval")}
                        </label>
                        <p className={styles.settingDescription}>
                          {t("settings.autoSave.intervalDescription")}
                        </p>
                      </div>
                      <select
                        className={styles.select}
                        value={settings.autoSave.intervalSeconds}
                        onChange={handleAutoSaveIntervalChange}
                      >
                        <option value="15">15 {t("settings.autoSave.seconds")}</option>
                        <option value="30">30 {t("settings.autoSave.seconds")}</option>
                        <option value="60">1 {t("settings.autoSave.minute")}</option>
                        <option value="120">2 {t("settings.autoSave.minutes")}</option>
                        <option value="300">5 {t("settings.autoSave.minutes")}</option>
                      </select>
                    </div>
                  )}
                </div>
              </>
            )}

            {activeTab === "ai" && (
              <>
                {/* Master AI toggle */}
                <div className={styles.section}>
                  <h2 className={styles.sectionTitle}>{t("settings.ai.masterToggle.title")}</h2>
                  <p className={styles.sectionDescription}>
                    {t("settings.ai.masterToggle.description")}
                  </p>

                  <div className={styles.settingRow}>
                    <div className={styles.settingInfo}>
                      <label className={styles.settingLabel}>
                        {t("settings.ai.masterToggle.label")}
                      </label>
                      <p className={styles.settingDescription}>
                        {settings.aiEnabled
                          ? t("settings.ai.masterToggle.enabledHint")
                          : t("settings.ai.masterToggle.disabledHint")}
                      </p>
                    </div>
                    <button
                      type="button"
                      className={`${styles.toggle} ${settings.aiEnabled ? styles.toggleOn : ""}`}
                      onClick={() => setAIEnabled(!settings.aiEnabled)}
                      aria-pressed={settings.aiEnabled}
                    >
                      <span className={styles.toggleThumb} />
                    </button>
                  </div>
                </div>

                {/* AI Connection + Behavior — hidden when AI is disabled */}
                {settings.aiEnabled && (
                  <>
                    <div className={styles.section}>
                      <h2 className={styles.sectionTitle}>{t("settings.apiConfig.title")}</h2>
                      <p className={styles.sectionDescription}>
                        {t("settings.apiConfig.description")}
                      </p>

                      {/* Connection fieldset */}
                      <fieldset className={styles.connectionFieldset}>
                        <legend className={styles.connectionLegend}>
                          {t("settings.apiConfig.connectionDetails")}
                        </legend>

                        <div className={styles.connectionFields}>
                          <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel}>
                              {t("settings.apiConfig.providerLabel")}
                            </label>
                            <select
                              className={`${styles.select} ${styles.connectionSelect}`}
                              value={selectedProvider}
                              onChange={(e) => {
                                const newProvider = e.target.value as AIProvider;
                                setSelectedProvider(newProvider);
                                setVerifyResult(null);
                                setApiKeyError(
                                  apiKeyInput.trim()
                                    ? validateApiKey(apiKeyInput, newProvider)
                                    : null,
                                );
                                if (newProvider === "custom" && !baseUrlInput.trim()) {
                                  setBaseUrlInput(DEFAULT_CUSTOM_BASE_URL);
                                }
                              }}
                            >
                              <option value="anthropic">
                                {t("settings.apiConfig.providerAnthropic")}
                              </option>
                              <option value="openai">
                                {t("settings.apiConfig.providerOpenAI")}
                              </option>
                              <option value="custom">
                                {t("settings.apiConfig.providerCustom")}
                              </option>
                            </select>
                          </div>

                          {selectedProvider === "custom" && (
                            <div className={styles.customProviderFields}>
                              <div className={styles.fieldGroup}>
                                <div className={styles.fieldLabelRow}>
                                  <label className={styles.fieldLabel}>
                                    {t("settings.apiConfig.baseUrlLabel")}
                                    <span className={styles.fieldLabelHint}> *</span>
                                  </label>
                                  {baseUrlInput !== DEFAULT_CUSTOM_BASE_URL &&
                                    baseUrlInput.trim() !== "" && (
                                      <button
                                        type="button"
                                        className={styles.resetLink}
                                        onClick={() => setBaseUrlInput(DEFAULT_CUSTOM_BASE_URL)}
                                      >
                                        {t("settings.apiConfig.resetToDefault", "Reset to default")}
                                      </button>
                                    )}
                                </div>
                                <input
                                  type="url"
                                  className={styles.textInput}
                                  value={baseUrlInput}
                                  onChange={(e) => setBaseUrlInput(e.target.value)}
                                  placeholder={t("settings.apiConfig.baseUrlPlaceholder")}
                                  autoComplete="off"
                                />
                                <p className={styles.fieldHint}>
                                  {t("settings.apiConfig.customProviderHint")}
                                </p>
                              </div>
                              <div className={styles.fieldGroup}>
                                <label className={styles.fieldLabel}>
                                  {t("settings.apiConfig.modelNameLabel")}
                                  <span className={styles.fieldLabelHint}> *</span>
                                </label>
                                <input
                                  type="text"
                                  className={styles.textInput}
                                  value={modelNameInput}
                                  onChange={(e) => setModelNameInput(e.target.value)}
                                  placeholder={t("settings.apiConfig.modelNamePlaceholder")}
                                  autoComplete="off"
                                />
                              </div>
                            </div>
                          )}

                          <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel}>
                              {t("settings.apiConfig.apiKeyLabel")}
                              {selectedProvider === "custom" && (
                                <span className={styles.fieldLabelHint}>
                                  {" "}
                                  ({t("settings.apiConfig.optional")})
                                </span>
                              )}
                            </label>
                            <div className={styles.apiKeyInputWrapper}>
                              <input
                                type={showApiKey ? "text" : "password"}
                                className={`${styles.textInput} ${styles.apiKeyField} ${apiKeyError ? styles.textInputError : ""}`}
                                value={apiKeyInput}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setApiKeyInput(val);
                                  setVerifyResult(null);
                                  setApiKeyError(
                                    val.trim() ? validateApiKey(val, selectedProvider) : null,
                                  );
                                }}
                                placeholder={
                                  aiConfigured && verifyResult && !verifyResult.valid
                                    ? t("settings.apiConfig.placeholderReplace")
                                    : aiConfigured
                                      ? "••••••••••••••••"
                                      : t(
                                          `settings.apiConfig.${PROVIDER_PLACEHOLDERS[selectedProvider]}`,
                                        )
                                }
                                autoComplete="off"
                              />
                              <button
                                type="button"
                                className={styles.visibilityToggle}
                                onClick={() => setShowApiKey(!showApiKey)}
                                aria-label={
                                  showApiKey
                                    ? t("settings.apiConfig.hideApiKey")
                                    : t("settings.apiConfig.showApiKey")
                                }
                                title={
                                  showApiKey
                                    ? t("settings.apiConfig.hideApiKey")
                                    : t("settings.apiConfig.showApiKey")
                                }
                              >
                                {showApiKey ? <EyeOffIcon /> : <EyeIcon />}
                              </button>
                            </div>
                            {apiKeyError && <p className={styles.fieldError}>✕ {apiKeyError}</p>}
                            {/* Inline verification feedback (Fixes #514) */}
                            {!apiKeyError &&
                              verifyResult !== null &&
                              (verifyResult.valid || userHasInteracted) && (
                                <p
                                  className={
                                    verifyResult.valid ? styles.verifySuccess : styles.verifyError
                                  }
                                >
                                  {verifyResult.valid
                                    ? t("settings.apiConfig.verifyValidMessage")
                                    : `${t("settings.apiConfig.verifyInvalidPrefix")} ${verifyResult.error ?? t("settings.apiConfig.verifyFailed")}${selectedProvider === "custom" ? "" : ` ${t("settings.apiConfig.verifyInvalidSuffix")}`}`}
                                </p>
                              )}
                            {PROVIDER_KEY_URLS[selectedProvider].url && (
                              <a
                                href={PROVIDER_KEY_URLS[selectedProvider].url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.getApiKeyLink}
                              >
                                {t(PROVIDER_KEY_URLS[selectedProvider].labelKey)}
                              </a>
                            )}
                          </div>
                        </div>
                      </fieldset>

                      {/* Actions + feedback — placed right below the fieldset */}
                      <div className={styles.aiConfigActions}>
                        <button
                          type="button"
                          className={styles.saveButton}
                          onClick={handleSaveApiKey}
                          disabled={
                            isSavingKey ||
                            !!apiKeyError ||
                            (selectedProvider !== "custom" && !apiKeyInput.trim()) ||
                            (selectedProvider === "custom" &&
                              (!baseUrlInput.trim() || !modelNameInput.trim()))
                          }
                        >
                          {isSavingKey
                            ? t("settings.apiConfig.validating")
                            : t("settings.apiConfig.save")}
                        </button>
                        <button
                          type="button"
                          className={styles.verifyButton}
                          onClick={handleTestConnection}
                          disabled={
                            isTesting ||
                            isSavingKey ||
                            !!apiKeyError ||
                            (selectedProvider !== "custom" &&
                              !apiKeyInput.trim() &&
                              !aiHasConfig) ||
                            (selectedProvider === "custom" &&
                              (!baseUrlInput.trim() || !modelNameInput.trim()))
                          }
                        >
                          {isTesting
                            ? t("settings.apiConfig.testing")
                            : t("settings.apiConfig.testConnection")}
                        </button>
                        {(aiConfigured || aiHasConfig) && (
                          <button
                            type="button"
                            className={styles.removeButton}
                            onClick={handleRemoveApiKey}
                            disabled={isSavingKey}
                          >
                            {t("settings.apiConfig.remove")}
                          </button>
                        )}

                        {/* Inline status badge */}
                        {(aiConfigured || aiHasConfig) &&
                          verifyResult !== null &&
                          !apiKeyError &&
                          (verifyResult.valid || userHasInteracted) && (
                            <span
                              className={`${styles.statusBadge} ${
                                verifyResult.valid ? styles.statusConnected : styles.statusWarning
                              }`}
                            >
                              {verifyResult.valid
                                ? t("settings.apiConfig.active")
                                : t("settings.apiConfig.needsUpdate")}
                            </span>
                          )}
                      </div>

                      {/* Verification feedback moved inline below API key field (#514) */}
                    </div>

                    {/* Behavior settings — hidden until a provider is configured */}
                    {aiHasConfig && (
                      <div className={styles.section}>
                        <h2 className={styles.sectionTitle}>{t("settings.aiBehavior.title")}</h2>
                        <p className={styles.sectionDescription}>
                          {t("settings.aiBehavior.description")}
                        </p>

                        <div className={styles.behaviorGrid}>
                          <div className={styles.behaviorCard}>
                            <div className={styles.behaviorCardHeader}>
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                width="18"
                                height="18"
                              >
                                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                              </svg>
                              <label className={styles.settingLabel}>
                                {t("settings.aiBehavior.frequency")}
                              </label>
                            </div>
                            <p className={styles.behaviorCardDescription}>
                              {t("settings.aiBehavior.frequencyDescription")}
                            </p>
                            <select
                              className={`${styles.select} ${styles.behaviorCardControl}`}
                              value={settings.aiBehavior.suggestionFrequency}
                              onChange={handleSuggestionFrequencyChange}
                            >
                              <option value="minimal">{t("settings.aiBehavior.minimal")}</option>
                              <option value="balanced">{t("settings.aiBehavior.balanced")}</option>
                              <option value="aggressive">
                                {t("settings.aiBehavior.aggressive")}
                              </option>
                            </select>
                          </div>

                          <div className={styles.behaviorCard}>
                            <div className={styles.behaviorCardHeader}>
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                width="18"
                                height="18"
                              >
                                <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                              </svg>
                              <label className={styles.settingLabel}>
                                {t("settings.aiBehavior.inlineSuggestions")}
                              </label>
                            </div>
                            <p className={styles.behaviorCardDescription}>
                              {t("settings.aiBehavior.inlineSuggestionsDescription")}
                            </p>
                            <div className={styles.behaviorCardControl}>
                              <button
                                type="button"
                                className={`${styles.toggle} ${settings.aiBehavior.showInlineSuggestions ? styles.toggleOn : ""}`}
                                onClick={handleInlineSuggestionsToggle}
                                aria-pressed={settings.aiBehavior.showInlineSuggestions}
                              >
                                <span className={styles.toggleThumb} />
                              </button>
                            </div>
                          </div>

                          <div className={styles.behaviorCard}>
                            <div className={styles.behaviorCardHeader}>
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                width="18"
                                height="18"
                              >
                                <polyline points="9 11 12 14 22 4" />
                                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                              </svg>
                              <label className={styles.settingLabel}>
                                {t("settings.aiBehavior.autoAccept")}
                              </label>
                            </div>
                            <p className={styles.behaviorCardDescription}>
                              {t("settings.aiBehavior.autoAcceptDescription")}
                            </p>
                            <div className={styles.behaviorCardControl}>
                              <button
                                type="button"
                                className={`${styles.toggle} ${settings.aiBehavior.autoAcceptMinor ? styles.toggleOn : ""}`}
                                onClick={handleAutoAcceptMinorToggle}
                                aria-pressed={settings.aiBehavior.autoAcceptMinor}
                              >
                                <span className={styles.toggleThumb} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {activeTab === "voice" && (
              <div className={styles.section}>
                <h2 className={styles.sectionTitle}>{t("settings.voiceProfile.title")}</h2>
                <p className={styles.sectionDescription}>
                  {t("settings.voiceProfile.description")}
                </p>

                <div className={styles.voiceExplainer}>
                  <div className={styles.voiceExplainerIcon}>
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      width="24"
                      height="24"
                    >
                      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                      <line x1="12" y1="19" x2="12" y2="23" />
                      <line x1="8" y1="23" x2="16" y2="23" />
                    </svg>
                  </div>
                  <div className={styles.voiceExplainerContent}>
                    <h3 className={styles.voiceExplainerTitle}>How Voice Profiling Works</h3>
                    <p className={styles.voiceExplainerText}>
                      As you write and respond to AI suggestions, Verso builds a profile of your
                      preferences — your tone (formal vs casual), sentence complexity, vocabulary
                      level, and favorite literary devices. This profile helps the AI tailor its
                      suggestions to your unique voice.
                    </p>
                  </div>
                </div>

                <div className={styles.settingRow}>
                  <div className={styles.settingInfo}>
                    <label className={styles.settingLabel}>
                      {t("settings.voiceProfile.configure")}
                    </label>
                    <p className={styles.settingDescription}>
                      View your learned preferences, acceptance patterns, and reset your profile if
                      needed.
                    </p>
                  </div>
                  <Link to="/voice-profile" className={styles.linkButton}>
                    {t("settings.voiceProfile.viewProfile")}
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </Link>
                </div>
              </div>
            )}

            {activeTab === "account" && (
              <>
                {/* Email Section */}
                <div className={styles.section}>
                  <h2 className={styles.sectionTitle}>Email Address</h2>
                  <p className={styles.sectionDescription}>
                    Update your email address. You&apos;ll need to verify the new address.
                  </p>

                  <div className={styles.settingRow}>
                    <div className={styles.settingInfo}>
                      <label className={styles.settingLabel}>Current Email</label>
                      <p className={styles.settingDescription}>{authContext?.user?.email ?? "—"}</p>
                    </div>
                  </div>

                  <div className={styles.settingRow}>
                    <div className={styles.settingInfo}>
                      <label className={styles.settingLabel}>New Email</label>
                    </div>
                    <input
                      type="email"
                      className={styles.textInput}
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="new@example.com"
                      autoComplete="email"
                    />
                  </div>

                  <div className={styles.settingRow}>
                    <div className={styles.settingInfo}>
                      <label className={styles.settingLabel}>Confirm Password</label>
                      <p className={styles.settingDescription}>Required to change your email</p>
                    </div>
                    <input
                      type="password"
                      className={styles.textInput}
                      value={emailPassword}
                      onChange={(e) => setEmailPassword(e.target.value)}
                      placeholder="Enter your password"
                      autoComplete="current-password"
                    />
                  </div>

                  <div className={styles.settingRow}>
                    <div className={styles.settingInfo} />
                    <button
                      type="button"
                      className={styles.saveButton}
                      onClick={handleUpdateEmail}
                      disabled={isUpdatingEmail || !newEmail.trim() || !emailPassword}
                    >
                      {isUpdatingEmail ? "Updating…" : "Update Email"}
                    </button>
                  </div>
                </div>

                {/* Password Section */}
                <div className={styles.section}>
                  <h2 className={styles.sectionTitle}>Change Password</h2>
                  <p className={styles.sectionDescription}>
                    Update your password. Use at least 8 characters.
                  </p>

                  <div className={styles.settingRow}>
                    <div className={styles.settingInfo}>
                      <label className={styles.settingLabel}>Current Password</label>
                    </div>
                    <input
                      type="password"
                      className={styles.textInput}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Current password"
                      autoComplete="current-password"
                    />
                  </div>

                  <div className={styles.settingRow}>
                    <div className={styles.settingInfo}>
                      <label className={styles.settingLabel}>New Password</label>
                    </div>
                    <input
                      type="password"
                      className={styles.textInput}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="New password"
                      autoComplete="new-password"
                    />
                  </div>

                  <div className={styles.settingRow}>
                    <div className={styles.settingInfo}>
                      <label className={styles.settingLabel}>Confirm New Password</label>
                    </div>
                    <input
                      type="password"
                      className={styles.textInput}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      autoComplete="new-password"
                    />
                  </div>

                  <div className={styles.settingRow}>
                    <div className={styles.settingInfo} />
                    <button
                      type="button"
                      className={styles.saveButton}
                      onClick={handleChangePassword}
                      disabled={
                        isChangingPassword || !currentPassword || !newPassword || !confirmPassword
                      }
                    >
                      {isChangingPassword ? "Changing…" : "Change Password"}
                    </button>
                  </div>
                </div>

                {/* Reset Settings */}
                <div className={styles.section}>
                  <h2 className={styles.sectionTitle}>{t("settings.reset.title")}</h2>
                  <p className={styles.sectionDescription}>{t("settings.reset.description")}</p>

                  <div className={styles.settingRow}>
                    <div className={styles.settingInfo}>
                      <label className={styles.settingLabel}>{t("settings.reset.action")}</label>
                      <p className={styles.settingDescription}>
                        {t("settings.reset.actionDescription")}
                      </p>
                    </div>
                    <button
                      type="button"
                      className={styles.resetButton}
                      onClick={handleResetToDefaults}
                    >
                      {t("settings.reset.button")}
                    </button>
                  </div>
                </div>

                {/* Danger Zone */}
                <div className={`${styles.section} ${styles.dangerSection}`}>
                  <h2 className={styles.sectionTitle}>Danger Zone</h2>
                  <p className={styles.sectionDescription}>
                    Permanently delete your account and all associated data. This action cannot be
                    undone.
                  </p>

                  <div className={styles.settingRow}>
                    <div className={styles.settingInfo}>
                      <label className={styles.settingLabel}>Password</label>
                      <p className={styles.settingDescription}>Confirm your password to proceed</p>
                    </div>
                    <input
                      type="password"
                      className={styles.textInput}
                      value={deletePassword}
                      onChange={(e) => setDeletePassword(e.target.value)}
                      placeholder="Enter your password"
                      autoComplete="current-password"
                    />
                  </div>

                  <div className={styles.settingRow}>
                    <div className={styles.settingInfo}>
                      <label className={styles.settingLabel}>Type DELETE to confirm</label>
                    </div>
                    <input
                      type="text"
                      className={styles.textInput}
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      placeholder="DELETE"
                      autoComplete="off"
                    />
                  </div>

                  <div className={styles.settingRow}>
                    <div className={styles.settingInfo} />
                    <button
                      type="button"
                      className={styles.dangerButton}
                      onClick={handleDeleteAccount}
                      disabled={
                        isDeletingAccount || deleteConfirmText !== "DELETE" || !deletePassword
                      }
                    >
                      {isDeletingAccount ? "Deleting…" : "Delete Account"}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function EyeIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}
